import {
  Action,
  AionzDialog,
  AionzSnackbar,
  ConfirmationModalService,
  ConfirmResult,
  DialogSize,
  EmptyContentComponent,
  Filter,
  FilterOperators,
  LoadingService,
  ModalTypes
} from '@aionzdev/design-system';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatePipe, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  PLATFORM_ID,
  signal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  auditTime,
  catchError,
  debounceTime,
  finalize,
  forkJoin,
  map,
  merge,
  of,
  Subject,
  switchMap,
  take,
  takeUntil
} from 'rxjs';
import { SelectorListComponent } from '../../../shared/selector-list/selector-list.component';
import { AbstractSelectorListService } from '../../../shared/selector-list/services/abstract-selector-list.service';
import { ProductIntegrationStatus, PurchaseRequestStatus } from '../../../enums';
import {
  AcceptPurchaseRequestDialogData,
  AcceptPurchaseRequestDialogResult
} from '../../../interfaces/accept-purchase-request-dialog-data';
import { BuyerItem } from '../../../interfaces/buyer-item.interface';
import {
  CustomerTradingFocDialogData,
  CustomerTradingFocItem
} from '../../../interfaces/customer-trading-foc-dialog-data';
import { ProductIntegrationDialogData } from '../../../interfaces/product-integration-dialog-data';
import { ProductIntegrationRetryEvent } from '../../../interfaces/product-integration-retry-event';
import { PurchaseRequestBranch } from '../../../interfaces/purchase-request-branch';
import { PurchaseRequestCardData } from '../../../interfaces/pruchase-request-card-data';
import { PurchaseItemStatus } from '../../../interfaces/purchase-request';
import { PurchaseRequestResponseDto } from '../../../interfaces/purchase-response.dto';
import { PurchasingEvent } from '../../../interfaces/purchasing-event';
import { TimePerStageMetrics } from '../../../interfaces/time-per-stage-metrics';
import { WebsocketService } from '@aionzdev/modules';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { BuyerSelectorService } from '../../../services/buyer-selector.service';
import { KanbanPreferencesService } from '../../../services/kanban-preferences.service';
import { ProductIntegrationNotificationService } from '../../../services/product-integration-notification.service';
import { ProductIntegrationNotificationComponent } from '../../atoms/product-integration-notification/product-integration-notification.component';
import { AcceptPurchaseRequestDialogComponent } from '../../components/accept-purchase-request-dialog/accept-purchase-request-dialog.component';
import { CostumersTradingFocListComponent } from '../../components/costumers-trading-foc-list/costumers-trading-foc-list.component';
import { KanbanBoardComponent } from '../../components/kanban-board/kanban-board.component';
import { KanbanKeyboardNavDirective } from '../../directives/kanban-keyboard-nav.directive';
import { ProductIntegrationDialogComponent } from '../../components/product-integration-dialog/product-integration-dialog.component';
import { PurchasingFiltersToolbarComponent } from '../../components/purchasing-filters-toolbar/purchasing-filters-toolbar.component';
import { DurationFormatService } from './services/duration-format.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AbstractAuthModuleService } from '@aionzdev/modules';
import { FindAllPurchasesRequestDto, OrderBy } from '../../../interfaces/find-purchase-request.dto';

@Component({
  selector: 'aionz-purchasing-manager',
  imports: [
    KanbanBoardComponent,
    KanbanKeyboardNavDirective,
    DragDropModule,
    ProductIntegrationNotificationComponent,
    PurchasingFiltersToolbarComponent,
    MatButtonModule,
    MatIconModule,
    EmptyContentComponent
  ],
  providers: [DatePipe],
  templateUrl: './purchasing-manager.component.html',
  styleUrl: './purchasing-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchasingManagerComponent implements AfterViewInit, OnDestroy {
  private _dialog = inject(AionzDialog);
  private _confirmationModal = inject(ConfirmationModalService);
  private _purchasingService = inject(AbstractPurchasingService);
  private _durationFormat = inject(DurationFormatService);
  private _snackBar = inject(AionzSnackbar);
  private _elementRef = inject(ElementRef);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _loadingService = inject(LoadingService);
  private _notificationService = inject(ProductIntegrationNotificationService);
  private _injector = inject(Injector);
  private _websocketService = inject(WebsocketService);
  private _authService = inject(AbstractAuthModuleService);
  private _resizeObserver?: ResizeObserver;
  private _destroy$ = new Subject<void>();
  private _websocketUpdate$ = new Subject<void>();
  private window = inject(DOCUMENT).defaultView;
  protected purchases = signal<PurchaseRequestResponseDto[]>([]);
  protected containerHeight = signal<number>(0);

  /**
   * Indica se a empresa (tenant) atual tem integração com o ERP ativa.
   * Default true = comportamento atual (mostra a coluna "Pendentes de
   * Integração"). É atualizado no ngAfterViewInit a partir do
   * AbstractPurchasingService; em caso de erro fica em true (fail-open).
   */
  private readonly _integracaoErpAtiva = signal(true);

  protected boards = computed<{ status: PurchaseRequestStatus; title: string }[]>(() => {
    const all = [
      {
        status: PurchaseRequestStatus.PENDING,
        title: 'Solicitações de compra (Em campanha)'
      },
      {
        status: PurchaseRequestStatus.ACCEPTED,
        title: 'Solicitações de compras (Aceitas)'
      },
      {
        status: PurchaseRequestStatus.IN_QUOTATION,
        title: 'Cotações em andamento'
      },
      {
        status: PurchaseRequestStatus.PENDING_INTEGRATION,
        title: 'Pendentes de Integração'
      },
      {
        status: PurchaseRequestStatus.AWAITING_FOR_APPROVAL,
        title: 'Aguardando Aprovação'
      },
      {
        status: PurchaseRequestStatus.QUOTED,
        title: 'Cotações Concluídas'
      }
    ];

    return this._integracaoErpAtiva()
      ? all
      : all.filter(board => board.status !== PurchaseRequestStatus.PENDING_INTEGRATION);
  });

  protected purchasesByStatus = signal<Record<PurchaseRequestStatus, PurchaseRequestCardData[]>>(
    {} as any
  );
  protected boardIds = computed(() => this.boards().map(board => board.status));

  /** Colunas recolhidas pelo usuário; preferência guardada no navegador. */
  private readonly _kanbanPreferences = inject(KanbanPreferencesService);
  protected collapsedStatuses = signal<PurchaseRequestStatus[]>(
    this._kanbanPreferences.get().collapsedStatuses ?? []
  );

  public isBoardCollapsed(status: PurchaseRequestStatus): boolean {
    return this.collapsedStatuses().includes(status);
  }

  public setBoardCollapsed(status: PurchaseRequestStatus, collapsed: boolean): void {
    const next = collapsed
      ? Array.from(new Set([...this.collapsedStatuses(), status]))
      : this.collapsedStatuses().filter(s => s !== status);
    this.collapsedStatuses.set(next);
    this._kanbanPreferences.patch({ collapsedStatuses: next });
  }
  protected averageSlaLabels = signal<Partial<Record<PurchaseRequestStatus, string>>>({});
  protected branches = signal<PurchaseRequestBranch[]>([]);
  protected buyers = signal<BuyerItem[]>([]);
  protected visibleBuyers = computed(() =>
    this.buyers().filter(buyer => buyer.id !== this.currentUserId())
  );
  protected isBuyersLoading = signal(false);
  /** Qualquer filtro fora do padrão, incluindo ordenação e busca (a toolbar mostra os chips). */
  protected hasActiveFilters = computed(
    () =>
      this.selectedBranchId() !== 'all' ||
      (this.selectedBuyerId() ?? 'all') !== 'all' ||
      this.selectedOrderBy() !== undefined ||
      this.globalSearchTerm().trim() !== ''
  );
  protected hasNoPurchases = computed(() => this.purchases().length === 0);

  protected selectedBranchId = signal<string | undefined>('all');
  protected selectedBuyerId = signal<string | undefined>('all');
  protected selectedOrderBy = signal<OrderBy | undefined>(undefined);
  protected currentUserId = signal<string | undefined>(undefined);
  protected globalSearchTerm = signal('');

  private isBuyersEnd = false;
  private buyersCurrentPage = 1;
  private readonly buyersTake = 10;

  private _reload$ = new Subject<void>();
  private _debouncedReload$ = new Subject<void>();
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  constructor(private _datePipe: DatePipe) {
    this.initFiltersFromUrl();
    this.setupBoardDataStream();

    this._authService
      .userAuthenticated()
      .pipe(takeUntil(this._destroy$))
      .subscribe(user => {
        this.currentUserId.set(user.id);
        if (this.selectedBuyerId() === user.id) {
          this.selectedBuyerId.set('me');
        }
        this._reload$.next();
      });

    this.loadBranches();
    this.loadBuyers();

    if (isPlatformBrowser(this.platformId)) {
      this.setupRealtimeUpdates();
    }

    if (isPlatformBrowser(this.platformId) && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => {
        this.updateContainerHeight();
      });
    }
  }

  private initFiltersFromUrl(): void {
    const params = this._route.snapshot.queryParams;

    if (params['branchId']) this.selectedBranchId.set(params['branchId']);
    if (params['approverId']) this.selectedBuyerId.set(params['approverId']);
    if (params['orderBy'] && params['orderDirection']) {
      this.selectedOrderBy.set({ key: params['orderBy'], order: params['orderDirection'] });
    }
  }

  private resolveBuyerId(): string | undefined {
    const selected = this.selectedBuyerId();
    if (selected === 'all') return undefined;
    if (selected === 'me' || !selected) return this.currentUserId();
    return selected;
  }

  private resolveBranchId(): string | undefined {
    const selected = this.selectedBranchId();
    return selected === 'all' ? undefined : selected;
  }

  private buildFilters(): Filter[] {
    const filters: Filter[] = [];
    const branchId = this.resolveBranchId();
    const buyerId = this.resolveBuyerId();

    if (branchId)
      filters.push({ input: 'enderecoId', operator: FilterOperators.equals, value: branchId });
    if (buyerId)
      filters.push({ input: 'approverId', operator: FilterOperators.equals, value: buyerId });

    return filters;
  }

  private buildFindAllDto(): FindAllPurchasesRequestDto | undefined {
    const filters = this.buildFilters();
    const orderBy = this.selectedOrderBy();

    if (!filters.length && !orderBy) return undefined;

    return {
      ...(filters.length ? { filters } : {}),
      ...(orderBy ? { orderBy } : {})
    };
  }

  private setupBoardDataStream(): void {
    merge(this._reload$, this._debouncedReload$.pipe(debounceTime(300)))
      .pipe(
        switchMap(() => this.fetchBoardData$()),
        takeUntil(this._destroy$)
      )
      .subscribe(({ purchases, sla }) => {
        if (purchases) {
          this.purchases.set(purchases);
          this.updatePurchasesByStatus();
        }
        if (sla) {
          this.applyAverageSlaLabels(sla);
        }
        this._loadingService.hideLoading();
      });
  }

  private fetchBoardData$() {
    this._loadingService.showLoading();
    const filters = this.buildFilters();

    return forkJoin({
      purchases: this._purchasingService.findAllPurchases(this.buildFindAllDto()).pipe(
        map(response => response.body || []),
        catchError(() => of(null))
      ),
      sla: this._purchasingService
        .getAverageSlaLabels(filters.length ? filters : undefined)
        .pipe(catchError(() => of(null)))
    });
  }

  private applyAverageSlaLabels(hoursByStatus: TimePerStageMetrics): void {
    const labels: Partial<Record<PurchaseRequestStatus, string>> = {};

    for (const [status, hours] of Object.entries(hoursByStatus)) {
      if (hours == null) continue;
      labels[status as PurchaseRequestStatus] = this._durationFormat.formatFromHours(hours);
    }

    this.averageSlaLabels.set(labels);
  }

  private updateQueryParams(): void {
    const orderBy = this.selectedOrderBy();
    const buyerId = this.selectedBuyerId();
    const branchId = this.selectedBranchId();

    const approverId =
      buyerId === 'all' ? 'all' : buyerId === 'me' ? (this.currentUserId() ?? null) : buyerId;

    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        branchId: branchId && branchId !== 'all' ? branchId : null,
        approverId,
        orderBy: orderBy?.key ?? null,
        orderDirection: orderBy?.order ?? null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private applyFilters(): void {
    this.updateQueryParams();
    this._debouncedReload$.next();
  }

  public handleOrderChange(orderBy: OrderBy | undefined): void {
    const current = this.selectedOrderBy();
    if (current?.key === orderBy?.key && current?.order === orderBy?.order) return;

    this.selectedOrderBy.set(orderBy);
    this.applyFilters();
  }

  public handleBranchFilterChange(branchId: string | undefined): void {
    this.selectedBranchId.set(branchId);
    this.applyFilters();
  }

  public handleBuyerFilterChange(buyerId: string | undefined): void {
    this.selectedBuyerId.set(buyerId);
    this.applyFilters();
  }

  public clearFilters(): void {
    this.selectedBranchId.set('all');
    this.selectedBuyerId.set('all');
    this.selectedOrderBy.set(undefined);
    this.globalSearchTerm.set('');
    this.applyFilters();
  }

  public handleSearchChange(term: string): void {
    this.globalSearchTerm.set(term);
  }

  private loadBranches(): void {
    this._purchasingService.getAddress().subscribe({
      next: branches => {
        this.branches.set(branches);
      }
    });
  }

  private loadBuyers(): void {
    if (this.isBuyersEnd) return;

    this.isBuyersLoading.set(true);
    this._purchasingService
      .findBuyersByGroupAndUserName({
        search: '',
        currentPage: this.buyersCurrentPage,
        take: this.buyersTake
      })
      .pipe(finalize(() => this.isBuyersLoading.set(false)))
      .subscribe({
        next: response => {
          this.buyers.update(prev => [...prev, ...response.items]);

          if (
            this.buyersCurrentPage >= response.pagesCount ||
            response.items.length < this.buyersTake
          ) {
            this.isBuyersEnd = true;
          }
        }
      });
  }

  public onBuyersScrolled(): void {
    if (this.isBuyersLoading() || this.isBuyersEnd) return;
    this.buyersCurrentPage++;
    this.loadBuyers();
  }

  private setupRealtimeUpdates(): void {
    const config = this._purchasingService.getRealtimeConfig();
    const updateTypes = new Set(config.updateTypes.board);

    this._websocketUpdate$.pipe(auditTime(500), takeUntil(this._destroy$)).subscribe(() => {
      this._reload$.next();
    });

    this._websocketService.sendMessage(config.joinEvent, config.tenantId);

    this._websocketService.listen(config.eventName, (event: any) => {
      const mappedEvent: PurchasingEvent = this._purchasingService.getPurchasingEvent(event);
      if (
        String(mappedEvent.tenantId) === String(config.tenantId) &&
        updateTypes.has(mappedEvent.type)
      ) {
        this._websocketUpdate$.next();
      }
    });
  }

  ngAfterViewInit(): void {
    this.loadCompanyIntegrationStatus();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this._resizeObserver && this.document.body) {
      this._resizeObserver.observe(this.document.body);
    }

    this.updateContainerHeight();
  }

  /**
   * Consulta o status da integração com o ERP para esconder a coluna
   * "Pendentes de Integração" quando a empresa não tem ERP configurado.
   * Fail-open: qualquer erro mantém _integracaoErpAtiva em true.
   */
  private loadCompanyIntegrationStatus(): void {
    this._purchasingService
      .getCompanyIntegrationStatus()
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        next: ({ integracaoErpAtiva }) => this._integracaoErpAtiva.set(integracaoErpAtiva),
        error: () => {
          /* fail-open: mantém a coluna visível (comportamento atual) */
        }
      });
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      const config = this._purchasingService.getRealtimeConfig();
      this._websocketService.sendMessage(config.leaveEvent, config.tenantId);
    }

    this._resizeObserver?.disconnect();
    this._destroy$.next();
    this._destroy$.complete();
    this._reload$.complete();
    this._debouncedReload$.complete();
  }

  private updateContainerHeight(): void {
    const element = this._elementRef.nativeElement;
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const topOffset = rect.top;
    const padding = 24;

    const availableHeight = viewportHeight - topOffset - padding * 2;
    this.containerHeight.set(Math.max(availableHeight, 400));
  }

  private updatePurchasesByStatus(): void {
    const newPurchasesByStatus: Record<PurchaseRequestStatus, PurchaseRequestCardData[]> =
      {} as any;

    this.boards().forEach(board => {
      newPurchasesByStatus[board.status] = [];
    });

    this.purchases().forEach(purchase => {
      const cardData = this.mapToCardData(purchase);
      purchase.status.forEach((status: PurchaseRequestStatus) => {
        if (newPurchasesByStatus[status]) {
          newPurchasesByStatus[status].push(cardData);
        }
      });
    });

    this.purchasesByStatus.set(newPurchasesByStatus);
  }

  public getPurchasesByStatus(status: PurchaseRequestStatus): PurchaseRequestCardData[] {
    return this.purchasesByStatus()[status] || [];
  }

  /**
   * Rota que o card abre, quando o clique é navegação (mesma regra de handleCardClicked).
   * Nos status que abrem dialog (integração, concluídas e a planilha legada de pendentes) não
   * há link e o card continua chamando handleCardClicked.
   */
  private cardLink(purchase: PurchaseRequestResponseDto): string[] | undefined {
    const status = purchase.status;
    if (status.includes(PurchaseRequestStatus.PENDING)) {
      const isLegacySpreadsheet = purchase.purchases?.some(p => !!p.file?.url);
      return isLegacySpreadsheet ? undefined : ['../solicitacao-pendente', purchase.id];
    }
    if (status.includes(PurchaseRequestStatus.ACCEPTED)) return ['../solicitacao-aceita', purchase.id];
    if (status.includes(PurchaseRequestStatus.IN_QUOTATION)) return ['../mapa-de-cotacao', purchase.id];
    if (status.includes(PurchaseRequestStatus.AWAITING_FOR_APPROVAL)) return ['../aprovacao', purchase.id];
    return undefined;
  }

  private mapToCardData(purchase: PurchaseRequestResponseDto): PurchaseRequestCardData {
    const formattedDate = this._datePipe.transform(
      purchase.requestedAt,
      "dd MMM yyyy 'às' HH:mm a",
      '',
      'pt-BR'
    );

    const requesters = purchase.purchases.map(p => p.requester);
    const file = purchase.purchases.map(p => p.file);
    const suppliers = Array.isArray(purchase.suppliers) ? purchase.suppliers : undefined;
    const pendingSuppliers = Array.isArray(purchase.pendingSuppliers)
      ? purchase.pendingSuppliers
      : undefined;

    return {
      id: purchase.id,
      status: purchase.status,
      title: purchase.title,
      description: purchase.description,
      requestedAt: purchase.requestedAt,
      requesters,
      footerText: `Solicitado em: ${formattedDate}`,
      hasDuplicatedProducts: purchase.hasDuplicatedProducts,
      suppliers,
      pendingSuppliers,
      hasPendingSuggestedSuppliers: purchase.hasPendingSuggestedSuppliers,
      remainingDays: purchase.remainingDays,
      isDeadlineWarning: purchase.isDeadlineWarning,
      buyer: {
        id: purchase.buyer?.id,
        responsibleName: purchase.buyer?.responsibleName,
        avatarUrl: purchase.buyer?.avatarUrl
      },
      file,
      link: this.cardLink(purchase),
      timePerStage: purchase.timePerStage,
      orders: purchase.orders,
      urlRelatorioAuditoria: purchase.urlRelatorioAuditoria,
      urlMapaCotacao: purchase.urlMapaCotacao,
      quotationMapPdfGeneratedAt: purchase.quotationMapPdfGeneratedAt
    };
  }

  private mapToApproveDialogData(
    purchase: PurchaseRequestResponseDto
  ): AcceptPurchaseRequestDialogData {
    return {
      description: purchase.description,
      title: purchase.title,
      id: purchase.id,
      requestedAt: purchase.requestedAt,
      status: purchase.status[0],
      requests: purchase.purchases.map(p => ({
        id: p.id,
        requestedAt: p.requestedAt,
        requester: p.requester,
        status: p.status,
        file: p.file
      }))
    };
  }

  public handleCardClicked(card: PurchaseRequestCardData): void {
    if (card.status.includes(PurchaseRequestStatus.PENDING)) {
      const purchase = this.purchases().find(p => p.id === card.id);
      const isLegacySpreadsheet =
        purchase?.purchases && purchase.purchases.some(p => !!p.file?.url);
      if (isLegacySpreadsheet) {
        this.openApproveDialog(card);
      } else {
        this.navigateToPendingPurchaseDetails(card.id);
      }
    } else if (card.status.includes(PurchaseRequestStatus.AWAITING_FOR_APPROVAL)) {
      // Abre o detalhe (itens, vencedores e valores); a aprovação é feita lá.
      this._router.navigate(['../aprovacao', card.id], {
        relativeTo: this._route,
        state: { returnUrl: this._router.url }
      });
    } else if (card.status.includes(PurchaseRequestStatus.ACCEPTED)) {
      this.navigateToPurchaseDetails(card.id);
    } else if (card.status.includes(PurchaseRequestStatus.IN_QUOTATION)) {
      this._router.navigate(['../mapa-de-cotacao', card.id], { relativeTo: this._route });
    } else if (card.status.includes(PurchaseRequestStatus.PENDING_INTEGRATION)) {
      this.openProductIntegrationDialog(card);
    } else if (card.status.includes(PurchaseRequestStatus.QUOTED)) {
      this.openCustomerTradingFocsDialog(card.id);
    }
  }

  private navigateToPendingPurchaseDetails(purchaseId: string): void {
    this._router.navigate(['../solicitacao-pendente', purchaseId], {
      relativeTo: this._route,
      queryParams: this._route.snapshot.queryParams,
      state: {
        returnUrl: this._router.url
      }
    });
  }

  public handleOpenAuditReport(card: PurchaseRequestCardData): void {
    this.openExternalPdf(card.urlRelatorioAuditoria);
  }

  public handleOpenQuotationMapPdf(card: PurchaseRequestCardData): void {
    this.openExternalPdf(card.urlMapaCotacao);
  }

  /** A URL é do S3 e não passa pela API, então abre em nova aba em vez de baixar por `HttpClient`. */
  private openExternalPdf(url?: string | null): void {
    if (!url) return;
    this.window?.open(url, '_blank', 'noopener,noreferrer');
  }

  private navigateToPurchaseDetails(purchaseId: string): void {
    this._router.navigate(['../solicitacao-aceita', purchaseId], {
      relativeTo: this._route,
      queryParams: this._route.snapshot.queryParams,
      state: {
        returnUrl: this._router.url
      }
    });
  }

  private openCustomerTradingFocsDialog(purchaseId: string): void {
    this._loadingService.showLoading();

    this._purchasingService
      .getCustomerTradingFocs(purchaseId)
      .pipe(finalize(() => this._loadingService.hideLoading()))
      .subscribe(focs => {
        if (!focs?.length) {
          this._snackBar.warning('Nenhuma FOC gerada para esta cotação.');
          return;
        }

        this.openCustomerTradingFocListDialog(focs);
      });
  }

  private openCustomerTradingFocListDialog(focs: CustomerTradingFocItem[]): void {
    if (focs.length === 1) {
      this.window?.open(focs[0].focUrl, '_blank');
    } else {
      this._dialog.open<CostumersTradingFocListComponent, CustomerTradingFocDialogData>(
        CostumersTradingFocListComponent,
        { data: { focs } }
      );
    }
  }

  private openApproveDialog(card: PurchaseRequestCardData): void {
    const purchase = this.purchases().find(p => p.id === card.id);
    if (!purchase) return;

    const dialogRef = this._dialog.open<
      AcceptPurchaseRequestDialogComponent,
      AcceptPurchaseRequestDialogData
    >(AcceptPurchaseRequestDialogComponent, {
      data: this.mapToApproveDialogData(purchase),
      size: DialogSize.LG
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.handleAcceptDialogResult(result);
      }
    });
  }

  private handleAcceptDialogResult(result: AcceptPurchaseRequestDialogResult[]): void {
    const newStatus = result.some(e => e.status == PurchaseItemStatus.APPROVED)
      ? PurchaseRequestStatus.ACCEPTED
      : PurchaseRequestStatus.PENDING;

    const showFeedback = newStatus === PurchaseRequestStatus.ACCEPTED;
    const requests = result.map(item =>
      this._purchasingService.updatePurchaseRequestStatus({
        id: item.id,
        status: item.status,
        reasonDecline: item.reason
      })
    );

    const source = requests.length > 0 ? forkJoin(requests) : of([]);

    this._loadingService.showLoading();
    source.pipe(finalize(() => this._loadingService.hideLoading())).subscribe({
      next: () => {
        this.showStatusUpdateFeedback(showFeedback);
      },
      error: () => {
        this.showStatusUpdateError();
      }
    });
  }

  private showStatusUpdateFeedback(isApproved: boolean): void {
    this._snackBar.success(
      isApproved ? 'Solicitação aceita com sucesso!' : 'Solicitação recusada com sucesso!'
    );
  }

  private showStatusUpdateError(): void {
    this._snackBar.error('Erro ao atualizar o status da solicitação');
  }

  private openProductIntegrationDialog(card: PurchaseRequestCardData): void {
    this._loadingService.showLoading();

    this._purchasingService
      .findUncompletedPurchases(card.id)
      .pipe(finalize(() => this._loadingService.hideLoading()))
      .subscribe({
        next: response => {
          const data = response.body;
          if (!data) return;

          const dialogRef = this._dialog.open<ProductIntegrationDialogComponent>(
            ProductIntegrationDialogComponent,
            {
              data
            }
          );

          dialogRef.componentInstance.onRetry.subscribe(event => {
            this.handleProductIntegrationRetry(event);
          });
        },
        error: () => {
          this._snackBar.error('Erro ao carregar detalhes da integração.');
        }
      });
  }

  private handleProductIntegrationRetry(event: ProductIntegrationRetryEvent): void {
    this._purchasingService
      .approveQuotationMap(
        { purchaseId: event.purchaseId, items: event.items },
        { hideGlobalError: true }
      )
      .pipe(
        catchError(() => of(null)),
        switchMap(() => this._purchasingService.findUncompletedPurchases(event.purchaseId)),
        finalize(() => event.setLoading(false))
      )
      .subscribe({
        next: response => this.onRetrySuccess(response?.body, event),
        error: () => this.onRetryFail()
      });
  }

  private onRetrySuccess(
    data: ProductIntegrationDialogData | null,
    event: ProductIntegrationRetryEvent
  ): void {
    if (
      !data ||
      !data.products.some(p => p.status === ProductIntegrationStatus.INTEGRATION_FAILED)
    ) {
      this._notificationService.show(
        ProductIntegrationStatus.SUCCESSFULLY_INTEGRATED,
        'Integração realizada com sucesso!'
      );
      this._dialog.closeAll();
    } else {
      this._notificationService.show(
        ProductIntegrationStatus.INTEGRATION_FAILED,
        'Alguns produtos continuam não completados. Veja o motivo em cada um.'
      );
      event.updateData(data);
    }
  }

  private onRetryFail(): void {
    this._notificationService.show(
      ProductIntegrationStatus.INTEGRATION_FAILED,
      'Falha ao carregar detalhes da integração.'
    );
    this._dialog.closeAll();
    this._reload$.next();
  }

  public handleChangeBuyer(card: PurchaseRequestCardData): void {
    const injector = Injector.create({
      providers: [
        {
          provide: AbstractSelectorListService,
          useClass: BuyerSelectorService
        }
      ],
      parent: this._injector
    });

    const dialogRef = this._dialog.open(SelectorListComponent, {
      data: {
        title: 'Alterar o comprador',
        placeholder: 'Pesquise aqui os compradores...',
        buttonIcon: 'swap_horiz',
        buttonLabel: 'Alterar',
        currentBuyerId: card.buyer?.id
      },
      injector,
      panelClass: 'seed-panel-dialog-big-flexible'
    });

    dialogRef.afterClosed().subscribe((buyer: BuyerItem) => {
      if (buyer) {
        this.updatePurchaseBuyer(card.id, buyer.id);
      }
    });
  }

  private updatePurchaseBuyer(purchaseId: string, buyerId: string): void {
    this._loadingService.showLoading();
    this._purchasingService
      .updateBuyer(purchaseId, buyerId)
      .pipe(finalize(() => this._loadingService.hideLoading()))
      .subscribe({
        next: () => {
          this._snackBar.success('Comprador alterado com sucesso!');
        },
        error: () => {
          this._snackBar.error('Erro ao alterar o comprador.');
        }
      });
  }

  public handleCancelPurchaseRequest(card: PurchaseRequestCardData): void {
    this._confirmationModal
      .openWithJustification(
        {
          headerTitle: 'Recusar solicitação',
          title: 'Motivo da recusa',
          description: '',
          labelConfirm: 'Desejo recusar e enviar motivo da recusa'
        },
        {
          justificationPlaceholder: 'Digite aqui o motivo pelo qual o pedido foi recusado.',
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .subscribe((result: ConfirmResult) => {
        if (result.action !== Action.CONFIRM || !result.justification) {
          return;
        }

        this._loadingService.showLoading();
        this._purchasingService
          .cancelPurchaseRequest(card.id, result.justification)
          .pipe(finalize(() => this._loadingService.hideLoading()))
          .subscribe({
            next: () => {
              this._snackBar.success('Solicitação cancelada com sucesso!');
            },
            error: () => {
              this._snackBar.error('Erro ao cancelar a solicitação.');
            }
          });
      });
  }

  public closeNotification(): void {
    this._notificationService.hide();
  }
}
